// Contrato de Originación — Portal Admin SureKeep.
//
// Espejo de `GET /backoffice/origination/analytics` de `retail-wizard-business`
// (`src/domain/entities/OriginationAnalytics.ts`). Es SILO-WIDE: el actor es el Admin SureKeep con
// scope TENANT y el endpoint NO acepta parámetros — ni cadena, ni `from`/`to`.
//
// Los TRES chips de ventana (7 / 30 / 90 días) viajan en UNA respuesta calculada de la misma
// lectura, así que mover el chip NO vuelve a pegarle a la API.
//
// DINERO: centavos enteros.
//
// 🔴 **PORCENTAJES en ESCALA DE PORCENTAJE — `42.3` es 42.3 %, NO 0.423.** El silo hace el
// `Math.round(x * 1000) / 10`; multiplicar de nuevo al pintar produce el `+10000%` que ya se pagó
// una vez en «Análisis de ventas». Los campos que terminan en `Pct` o `Pp` NO se re-escalan.
//
// 🔴 **`null` NO es cero.** `capturePct: null` es «no hubo ventas que dividir», no «capturamos 0 %».
// Se pinta como guion con su explicación, nunca como número.

/** Los tres chips del selector. Las llaves son las del silo, no se traducen. */
export type OriginationRangeKey = '7d' | '30d' | '90d';

export const ORIGINATION_RANGE_KEYS: OriginationRangeKey[] = ['7d', '30d', '90d'];

export const ORIGINATION_RANGE_LABEL: Record<OriginationRangeKey, string> = {
    '7d': '7 días',
    '30d': '30 días',
    '90d': '90 días',
};

export const ORIGINATION_RANGE_DAYS: Record<OriginationRangeKey, number> = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
};

/** Piezas y monto de un canal. */
export interface ChannelTotals {
    units: number;
    cents: number;
}

/** Las ventas partidas en los tres canales del cuadro de tendencia, más su total. */
export interface ChannelBreakdown {
    sureKeep: ChannelTotals;
    otherFinanciers: ChannelTotals;
    cash: ChannelTotals;
    total: ChannelTotals;
}

/** Un punto de la tendencia. `day` es la fecha del negocio (CDMX) en `YYYY-MM-DD`. */
export interface OriginationDailyPoint extends ChannelBreakdown {
    day: string;
}

/** KPI «Volumen originado SK». `unitsChangePct` es RELATIVO y `null` sin base anterior. */
export interface OriginationVolumeKpi {
    units: number;
    cents: number;
    previousUnits: number;
    unitsChangePct: number | null;
}

/** KPI «Captura del mercado»: qué parte de las ventas del silo financió SureKeep. */
export interface MarketCaptureKpi {
    /** 0–100 con un decimal. `null` sin ventas: no hay base que dividir. */
    capturePct: number | null;
    previousCapturePct: number | null;
    /** Δ en PUNTOS PORCENTUALES (`capturePct − previousCapturePct`), nunca en % relativo. */
    changePp: number | null;
    sureKeepUnits: number;
    totalUnits: number;
}

/**
 * KPI «Cosecha: 1ª cuota a 14d»: de los créditos con la antigüedad suficiente, cuántos pagaron.
 *
 * 🔴 `cohortPct: null` es **N/D**, nunca un `0 %`. Un cero diría «todos fallaron»; el `null` dice
 * «ningún crédito de la ventana cumplió los 14 días todavía», que es otra cosa.
 */
export interface FirstInstallmentCohortKpi {
    /** Cumplieron sobre elegibles, en 0–100 con un decimal. `null` sin elegibles. */
    cohortPct: number | null;
    previousCohortPct: number | null;
    /** Δ en PUNTOS PORCENTUALES (`cohortPct − previousCohortPct`), nunca en % relativo. */
    changePp: number | null;
    /** Créditos con la antigüedad suficiente. Es el divisor y lo que se rotula abajo del KPI. */
    eligibleCredits: number;
    paidCredits: number;
    previousEligibleCredits: number;
}

/** Los cuatro cuadrantes del scatter de tiendas. Las llaves son las del silo, no se traducen. */
export type StoreQuadrant = 'LUCKY' | 'MODEL' | 'PROBLEM' | 'SOMETHING_OFF';

export const STORE_QUADRANTS: StoreQuadrant[] = ['LUCKY', 'MODEL', 'PROBLEM', 'SOMETHING_OFF'];

/**
 * Un punto del scatter de tiendas (C5 derecha): una tienda por punto.
 *
 * El `quadrant` viene RESUELTO por el silo con los cortes del mockup (SCI 60, cosecha 60 %). El
 * front dibuja las zonas con esos mismos cortes, pero no vuelve a clasificar: una tienda no puede
 * caer en un cuadrante acá y en otro en el tablero de al lado.
 */
export interface StoreCohortPoint {
    storeId: string;
    storeName: string | null;
    /** Eje X: SCI promedio de la tienda, sin empleados. */
    sciAvg: number | null;
    /** Eje Y: cosecha en 0–100. */
    cohortPct: number | null;
    /** Radio del punto: piezas colocadas con SureKeep. */
    sureKeepUnits: number;
    /** Color del punto: captura en 0–100. */
    capturePct: number | null;
    /** `null` cuando falta cualquiera de los dos ejes: un punto sin coordenada no se ubica. */
    quadrant: StoreQuadrant | null;
}

/** Una celda del mix: cuántas piezas colocó esa tienda con esa financiera. */
export interface OriginationFinancierCell {
    /** La llave del silo tal cual (`SUREKEEP`, `PAYJOY`, …). La `NONE` no viaja: no es financiera. */
    financier: string;
    units: number;
}

/** Renglón del ranking por tienda. Sin «Aprob.»: ese dato todavía no existe. */
export interface StoreOriginationRow {
    storeId: string;
    storeName: string | null;
    zoneName: string | null;
    channels: ChannelBreakdown;
    capturePct: number | null;
    captureChangePp: number | null;
    financierMix: OriginationFinancierCell[];
    /** Derivadas sin proveedor registrado: cuentan como otra financiera pero no tienen columna. */
    unknownFinancierUnits: number;
    /** Enganche promedio en 0–100, o `null` sin muestras. */
    downPaymentAvgPct: number | null;
    /**
     * Mínimo que el plan le exigía a cada crédito de esa tienda, promediado por su mezcla real.
     * Sale del catálogo de planes. `null` cuando no se pudieron leer los créditos.
     */
    minDownPaymentAvgPct: number | null;
    /** Δ en PUNTOS PORCENTUALES entre lo que cobró y su mínimo. Positivo = cobró de más. */
    downPaymentOverMinPp: number | null;
    /** Cosecha de la tienda en 0–100, o `null` sin elegibles. Columna «1ª cuota 14d». */
    firstInstallmentCohortPct: number | null;
    /** El divisor de la cosecha. Con muy pocos, el porcentaje dice poco: se rotula al lado. */
    firstInstallmentEligibleCredits: number;
    /** SCI promedio de la tienda EXCLUYENDO empleados, o `null` sin muestras. Eje X del scatter. */
    sciAvg: number | null;
}

/** Una barra del cuadro «Cómo originamos»: cuántos créditos de ese nivel y con qué enganche. */
export interface RiskLevelCell {
    /** La llave del silo tal cual (`BRONZE`, `SILVER`, `GOLD`). No se traduce en el contrato. */
    level: string;
    credits: number;
    /** Parte del total CON nivel, en 0–100. Los empleados NO entran en la base. */
    pct: number | null;
    downPaymentAvgPct: number | null;
    /** Mínimo del plan de esos créditos, en 0–100. Sale del catálogo. */
    minDownPaymentAvgPct: number | null;
    downPaymentOverMinPp: number | null;
}

/** Un punto del scatter. Sin PII: ni nombre ni id de cliente, solo SCI y lo que se financió. */
export interface SciAmountPoint {
    sci: number;
    /** Precio del equipo menos el enganche, en centavos. */
    financedCents: number;
}

/**
 * El cuadro C5 de una ventana: mezcla por nivel de riesgo y nube de SCI contra monto.
 *
 * 🔴 **«Empleado» NO es un nivel.** Se detecta por el plan y viaja en `employeeCredits`, fuera de
 * `levels`, de la base de los porcentajes y del SCI promedio. Pintarlo como una cuarta barra diría
 * que el motor le asignó un nivel de riesgo que nunca tuvo.
 */
export interface OriginationRiskMix {
    levels: RiskLevelCell[];
    /** Créditos con nivel conocido. Es la base de los porcentajes. */
    totalLeveled: number;
    employeeCredits: number;
    /** Créditos sin nivel congelado al originar. Se cuentan aparte; no se les inventa uno. */
    unknownLevelCredits: number;
    /** SCI promedio EXCLUYENDO empleados, o `null` sin muestras. */
    sciAvg: number | null;
    scatter: SciAmountPoint[];
    /** `true` si la ventana tenía más puntos que el tope del scatter. */
    scatterTruncated: boolean;
}

/** Los cuadros de un chip de ventana. */
export interface OriginationRangeBlocks {
    /** La ventana que el silo leyó, en ISO. Cortada en medianoche de CDMX, no de UTC. */
    from: string;
    to: string;
    daily: OriginationDailyPoint[];
    volume: OriginationVolumeKpi;
    marketCapture: MarketCaptureKpi;
    storeRanking: StoreOriginationRow[];
    riskMix: OriginationRiskMix;
    firstInstallmentCohort: FirstInstallmentCohortKpi;
    /** El scatter de tiendas del C5, en el mismo orden que el ranking. */
    storeScatter: StoreCohortPoint[];
}

export interface OriginationAnalyticsResponse {
    /** Los TRES chips, de la misma lectura. Cambiar de chip NO vuelve a pegarle a la API. */
    ranges: Record<string, OriginationRangeBlocks>;
    /** Cuántas cadenas del silo se barrieron. */
    retailersScanned: number;
    /**
     * `false` si el padrón de cadenas no respondió. El tablero está **DEGRADADO, no vacío**: los
     * ceros de abajo son «no se leyó», no «no hubo ventas». La pantalla tiene que decirlo.
     */
    rosterAvailable: boolean;
    /**
     * Truncado POR VENTANA. Si la actual se cortó y la anterior no, las variaciones no son
     * «aproximadas», son basura — y la pantalla tiene que poder distinguir los dos casos.
     */
    truncatedCurrent: boolean;
    truncatedPrevious: boolean;
    /**
     * `false` si `loan-credit` no respondió: C5 y el mínimo del enganche viajan VACÍOS, no en cero.
     * Es el mismo criterio que `rosterAvailable` — degradado, y la pantalla tiene que decirlo.
     */
    riskMixAvailable: boolean;
    /**
     * `false` si el catálogo de planes no respondió. Sin él no se puede separar al empleado ni
     * decir cuál era el mínimo, así que C5 y el mínimo del enganche también viajan vacíos.
     */
    plansAvailable: boolean;
    /** `true` si la ventana tenía más créditos que el tope: C5 salió de una parte, no del total. */
    creditsTruncated: boolean;
    /**
     * `false` si el motor no sirvió el estado de la primera cuota. La cosecha viaja VACÍA
     * (elegibles en 0 y porcentajes en `null`) y el resto del tablero sigue intacto: es
     * **DEGRADADO, no vacío**, y la pantalla tiene que decirlo.
     */
    firstInstallmentCohortAvailable: boolean;
    /** `true` si la ventana ANTERIOR se cortó: el Δ de la cosecha es parcial, no comparable. */
    cohortPreviousTruncated: boolean;
}

export interface OriginationApi {
    /** Sin parámetros: el silo entero, y los tres chips siempre juntos. */
    getOriginationAnalytics(): Promise<OriginationAnalyticsResponse>;
}
